import { readUnknownProperty } from "@shared/validation/narrow";
import type { RecordColumnDef } from "@/components/ui/record-table/column-types";
import type { BrowseListing } from "@/components/listing/browse/marketplace-listing";

function hiddenByDefaultColumnIds(
  columns: RecordColumnDef<BrowseListing, unknown>[],
): string[] {
  return columns.flatMap((column) =>
    readUnknownProperty(column.meta, "hiddenByDefault") === true && column.id
      ? [column.id]
      : [],
  );
}

const SPARSE_SPEC_MIN_ROWS = 5;
const SPARSE_SPEC_FILLED_SHARE = 1 / 3;

function isFilledSpec(value: unknown): boolean {
  return value !== null && value !== undefined && value !== "";
}

function sparseSpecColumnIds(
  columns: RecordColumnDef<BrowseListing, unknown>[],
  products: BrowseListing[],
): string[] {
  if (products.length < SPARSE_SPEC_MIN_ROWS) return [];
  return columns.flatMap((column) => {
    if (readUnknownProperty(column.meta, "isSpec") !== true || !column.id) return [];
    if (!("accessorFn" in column) || typeof column.accessorFn !== "function") return [];
    const accessor = column.accessorFn;
    const filled = products.filter((product, index) => isFilledSpec(accessor(product, index))).length;
    return filled / products.length < SPARSE_SPEC_FILLED_SHARE ? [column.id] : [];
  });
}

export function effectiveColumnVisibility(
  columns: RecordColumnDef<BrowseListing, unknown>[],
  products: BrowseListing[],
  visibility: Record<string, boolean>,
): Record<string, boolean> {
  const hiddenUntilChosen = [
    ...hiddenByDefaultColumnIds(columns),
    ...sparseSpecColumnIds(columns, products),
  ];
  return {
    ...Object.fromEntries(hiddenUntilChosen.map((id) => [id, false])),
    ...visibility,
  };
}
